// Loads a room package: the Draco-compressed model, one baked atlas per mesh,
// and the manifest. Baked meshes are unlit: the atlas already holds the
// Cycles lighting with the display transform applied.

import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { validateRoomManifest, type RoomManifest } from "../room/contract.ts";
import { DEVICES } from "../core/catalog.ts";
import type { LiveShadows } from "./live-shadows.ts";

export interface RoomInstance {
  manifest: RoomManifest;
  group: THREE.Group;
  glows: THREE.Sprite[];
  dispose(): void;
}

let glowTexture: THREE.Texture | null = null;

/** A soft additive halo, the kind drawn over a lamp. */
export function makeGlow(color: THREE.ColorRepresentation, size: number): THREE.Sprite {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: softDisc(),
    color,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    transparent: true,
    toneMapped: false,
    opacity: 0.85,
  }));
  sprite.scale.setScalar(size);
  sprite.userData.size = size;
  return sprite;
}

function softDisc(): THREE.Texture {
  if (glowTexture) return glowTexture;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const g = canvas.getContext("2d")!;
  const gradient = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, "rgba(255,255,255,0.55)");
  gradient.addColorStop(0.25, "rgba(255,255,255,0.22)");
  gradient.addColorStop(0.6, "rgba(255,255,255,0.05)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = gradient;
  g.fillRect(0, 0, size, size);
  glowTexture = new THREE.CanvasTexture(canvas);
  glowTexture.colorSpace = THREE.SRGBColorSpace;
  return glowTexture;
}

export async function loadRoom(baseUrl: string, renderer: THREE.WebGLRenderer, dracoPath: string, shadows: LiveShadows): Promise<RoomInstance> {
  const response = await fetch(baseUrl + "room.json");
  if (!response.ok) throw new Error(`room manifest not found at ${baseUrl}`);
  const manifest = validateRoomManifest(await response.json(), DEVICES.map((device) => device.rests));

  const draco = new DRACOLoader().setDecoderPath(dracoPath);
  const loader = new GLTFLoader().setDRACOLoader(draco);
  const textures = new THREE.TextureLoader();
  const anisotropy = renderer.capabilities.getMaxAnisotropy();

  const [gltf, ...maps] = await Promise.all([
    loader.loadAsync(baseUrl + manifest.model),
    ...Object.entries(manifest.atlases).map(async ([name, file]) => {
      const [map, shade] = await Promise.all([textures.loadAsync(baseUrl + file), textures.loadAsync(baseUrl + manifest.shade[name]!)]);
      map.colorSpace = THREE.SRGBColorSpace;
      map.flipY = false;
      map.anisotropy = anisotropy;
      // The shade atlas is a factor, not a colour: it is read as stored.
      shade.colorSpace = THREE.NoColorSpace;
      shade.flipY = false;
      return [name, { map, shade }] as const;
    }),
  ]);
  draco.dispose();
  const atlases = new Map(maps);

  const group = gltf.scene;
  group.name = `room:${manifest.id}`;
  const unbound = new Set(atlases.keys());
  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const name = (mesh.material as THREE.Material).name;
    const atlas = atlases.get(name);
    if (!atlas) throw new Error(`room mesh "${mesh.name}" names atlas "${name}", which the manifest does not list`);
    unbound.delete(name);
    const material = new THREE.MeshBasicMaterial({ name, map: atlas.map, toneMapped: false });
    shadows.receive(material, atlas.shade);
    mesh.material = material;
    // The room shades the live objects that stand in it.
    mesh.castShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
  });
  if (unbound.size) throw new Error(`room atlases without a mesh: ${[...unbound].join(", ")}`);

  const glows = manifest.glows.map((glow) => {
    const sprite = makeGlow(glow.color, glow.size);
    sprite.position.fromArray(glow.position);
    group.add(sprite);
    return sprite;
  });

  return {
    manifest,
    group,
    glows,
    dispose() {
      group.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      });
      for (const atlas of atlases.values()) {
        atlas.map.dispose();
        atlas.shade.dispose();
      }
      for (const sprite of glows) sprite.material.dispose();
    },
  };
}
